/* maaho-collection-tiles.js - the collection page: filtering, sorting, paging by
   TILE, and the mobile filter drawer.

   A tile is a product, or one fabric of a fabric-split product. "Products per
   page" means tiles: set 36 and the grid holds 36, whether that is 36 products or
   20 products and 16 fabrics of one. Shopify cannot do this - it pages by product,
   and a split listing is one product and many tiles - so this file does it in the
   browser from the tile index the section links in data-tile-index.

   The index is data, not markup: about nine compressed bytes per tile, so a
   2.280-tile collection is 21 KB gzipped, fetched once off the CDN and cached for
   a year. Only the tiles on screen are ever built into elements. Measured on a 6×
   throttled CPU: parse 3 ms, filter 3 ms, sort 1 ms, render a page 7 ms, with no
   network request per click.

   Nothing here slugs a label. A filter value's slug is stored on its Category
   filter metaobject and is the same string in every language; the label beside it
   is translated. Deriving the slug from the label at render time is what made
   every filter dead outside Denmark.

   Unfiltered page 1 is server-rendered HTML and is left untouched, so first paint
   and Google are unaffected. A filtered or deeper URL is redrawn on load, because
   only this side can count tiles. If the index is missing or fails to load,
   nothing here runs and the page keeps Shopify's own product paging - every link
   still works, it is just a full page load.

   Reads:  data-tile-index, data-tiles-per-page and data-cdn-files on #grid, the
           facet tags in the URL path, ?sort_by / ?page, and the maahoTile* and
           maahoFilterStrings globals the section renders.
   Writes: #grid, the count, the chips, the pagination, the sidebar's chosen state
           and the URL. */

/* The description expander. Its own block, above everything else, so it still
   binds on a page where the grid is missing and the rest of this file returns
   early. Labels come from data-* - Liquid does not run in an asset. */
(function () {
  'use strict';

  var toggle = document.querySelector('.collection-description-toggle');
  if (!toggle) return;

  var description = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!description) return;

  toggle.addEventListener('click', function () {
    var expanded = description.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded
      ? toggle.getAttribute('data-label-expanded')
      : toggle.getAttribute('data-label-collapsed');
  });
}());

(function () {
  'use strict';

  var grid = document.querySelector('[data-collection-filters]');
  if (!grid) return;

  var root = grid.closest('.collection-page') || document;
  var strings = window.maahoFilterStrings || {};

  /* The sample banner. Held by reference only - clearing the grid detaches this
     node but does not destroy it, so draw() can put the same element back. It is
     server-rendered markup carrying translated text and a CDN image, and nothing
     in the tile index describes it, so re-inserting beats rebuilding it here.

     Left in place on a page that never redraws.

     BANNER_AFTER_TILE must match banner_after_tile in
     sections/maaho-collection.liquid, or a filtered page moves the banner. */
  var BANNER_AFTER_TILE = 4;
  var banner = grid.querySelector('.maaho-banner');

  /* A fabric close-up is 78-80 KB against ~25 KB for the tile itself, and a touch
     screen never shows it. snippets/maaho-collection-card.liquid therefore leaves
     the hover image without a src; it is filled in here, and only where the device
     actually hovers. Cards drawn by card() skip it entirely. */
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function loadHoverImages(scope) {
    if (!canHover) return;
    (scope || document).querySelectorAll('img[data-hover-src]').forEach(function (picture) {
      picture.src = picture.getAttribute('data-hover-src');
      picture.removeAttribute('data-hover-src');
    });
  }
  loadHoverImages();

  bindDrawer();

  var indexUrl = grid.getAttribute('data-tile-index');
  if (!indexUrl) return;

  var perPage = parseInt(grid.getAttribute('data-tiles-per-page'), 10) || 36;
  var cdn = grid.getAttribute('data-cdn-files') || '';

  var index = null;
  var tiles = [];

  /* --- The index ----------------------------------------------------------- */

  /* Flat [productIndex, variantIndex] pairs - variantIndex -1 for an unsplit
     product. Everything else reads through to index.p[productIndex].

     index.o is the collection's own tile order, built from PrestaShop's manual
     arrangement. Filtering and paging both keep this order, so it only has to be
     right here. Without it the grid keeps Shopify's collection order. */
  function flatten() {
    if (index.o) {
      tiles = index.o;
      return;
    }
    tiles = [];
    for (var p = 0; p < index.p.length; p++) {
      var entry = index.p[p];
      if (entry.v) {
        for (var v = 0; v < entry.v.length; v++) tiles.push([p, v]);
      } else {
        tiles.push([p, -1]);
      }
    }
  }

  function load() {
    return fetch(indexUrl)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (payload) {
        index = payload;
        flatten();
        return index;
      });
  }

  function ensure() {
    return index ? Promise.resolve() : load();
  }

  /* --- Reading the URL ------------------------------------------------------ */

  /* Filters are the tags in the path, /collections/hynder/colour-bla+material-uld,
     the same URLs the server-rendered sidebar links to. */
  function readState() {
    var path = window.location.pathname.split('/');
    var collectionAt = path.indexOf('collections');
    var tagSegment = collectionAt >= 0 ? path[collectionAt + 2] : '';
    var parameters = new URLSearchParams(window.location.search);
    return {
      tags: tagSegment ? decodeURIComponent(tagSegment).split('+').filter(Boolean) : [],
      page: parseInt(parameters.get('page'), 10) || 1,
      sort: parameters.get('sort_by') || ''
    };
  }

  /* A tag is '<facet key>-<value slug>'. The longest matching key wins, so
     'fabric_family' is not read as 'fabric'. */
  function splitTag(tag) {
    var best = null;
    for (var i = 0; i < index.k.length; i++) {
      var key = index.k[i];
      if (tag.indexOf(key + '-') === 0 && (!best || key.length > best.length)) best = key;
    }
    return best ? {key: best, value: tag.slice(best.length + 1)} : null;
  }

  /* --- Filtering ------------------------------------------------------------ */

  /* index.fv holds slugs, so this is a comparison and never a conversion. */
  function slugOf(key, slot) {
    var values = index.fv[key];
    return values && values[slot] !== undefined ? values[slot] : '';
  }

  function filter(state) {
    if (!state.tags.length) return tiles;

    /* Values of one facet OR, different facets AND - PrestaShop's behaviour. */
    var wanted = {};
    state.tags.forEach(function (tag) {
      var part = splitTag(tag);
      if (!part) return;
      (wanted[part.key] = wanted[part.key] || []).push(part.value);
    });

    var keys = Object.keys(wanted);
    if (!keys.length) return tiles;

    return tiles.filter(function (tile) {
      var entry = index.p[tile[0]];
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];

        /* A colour belongs to the fabric, not the product, so a split tile is
           judged on its own fabric's colours - filtering by Sort must leave the
           black fabrics, not every fabric of a product that has one. A fabric
           with none recorded answers no colour filter rather than all of them. */
        var slots = entry.f[key] || [];
        if (key === 'colour' && tile[1] >= 0) {
          slots = entry.v[tile[1]][3];
        }

        var matched = slots.some(function (slot) {
          return wanted[key].indexOf(slugOf(key, slot)) !== -1;
        });
        if (!matched) return false;
      }
      return true;
    });
  }

  /* --- Sort dropdown --------------------------------------------------------
     Built from the <select> the section renders rather than instead of it: that
     element stays the value, so the change handler below and a browser with no
     JavaScript both keep working. The select is hidden once this takes over.

     Not used on a touch screen - there the platform picker is a full-height wheel
     that beats any list we could draw, and it is already there. */

  function buildSortDropdown() {
    var select = document.querySelector('[data-filter-sort]');
    if (!select || select.dataset.upgraded || !window.matchMedia('(hover: hover)').matches) return;
    select.dataset.upgraded = 'true';

    var dropdown = element('div', 'collection-sort-dropdown');
    var toggle = element('button', 'collection-sort-dropdown-toggle');
    toggle.type = 'button';
    toggle.setAttribute('aria-haspopup', 'listbox');
    toggle.setAttribute('aria-expanded', 'false');

    var list = element('div', 'collection-sort-dropdown-list');
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    function label() {
      return select.options[select.selectedIndex] ? select.options[select.selectedIndex].text : '';
    }
    function paint() {
      toggle.textContent = label();
      Array.prototype.forEach.call(list.children, function (row) {
        var chosen = row.dataset.value === select.value;
        row.classList.toggle('is-chosen', chosen);
        row.setAttribute('aria-selected', chosen ? 'true' : 'false');
      });
    }
    function setOpen(open) {
      list.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* Keeps the toggle's border dark while the list is down - see
         .collection-sort-dropdown[data-open] in maaho-collection-filters.css. */
      if (open) dropdown.dataset.open = 'true';
      else delete dropdown.dataset.open;
    }
    function close() { setOpen(false); }

    Array.prototype.forEach.call(select.options, function (option) {
      var row = element('button', 'collection-sort-dropdown-option', option.text);
      row.type = 'button';
      row.setAttribute('role', 'option');
      row.dataset.value = option.value;
      row.addEventListener('click', function () {
        select.value = option.value;
        paint();
        close();
        toggle.focus();
        /* The same event the select would have fired, so one handler drives both. */
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      list.appendChild(row);
    });

    toggle.addEventListener('click', function () { setOpen(list.hidden); });
    document.addEventListener('click', function (event) {
      if (!dropdown.contains(event.target)) close();
    });
    dropdown.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !list.hidden) {
        close();
        toggle.focus();
      }
    });

    dropdown.appendChild(toggle);
    dropdown.appendChild(list);
    select.parentNode.insertBefore(dropdown, select);
    select.hidden = true;
    paint();
  }

  /* --- Ordering ------------------------------------------------------------- */

  /* 'interleave' takes one fabric from each product in turn, so a colour-split
     catalogue reads as a mix rather than fifty of the same cushion in a row. */
  function order(list, sort) {
    if (sort === 'price-ascending') {
      return list.slice().sort(function (a, b) { return priceOf(a) - priceOf(b); });
    }
    if (sort === 'price-descending') {
      return list.slice().sort(function (a, b) { return priceOf(b) - priceOf(a); });
    }
    if (sort === 'title-ascending' || sort === 'title-descending') {
      /* localeCompare, not <: 'Å' sorts after 'Z' in Danish and before it in the
         default collation, so the alphabet the shopper sees depends on this. */
      var locale = document.documentElement.lang || 'da';
      var direction = sort === 'title-ascending' ? 1 : -1;
      return list.slice().sort(function (a, b) {
        return direction * titleOf(a).localeCompare(titleOf(b), locale);
      });
    }
    if (sort === 'discount-descending') {
      return list.slice().sort(function (a, b) { return discountOf(b) - discountOf(a); });
    }
    if (sort === 'interleave') {
      var byProduct = new Map();
      list.forEach(function (tile) {
        if (!byProduct.has(tile[0])) byProduct.set(tile[0], []);
        byProduct.get(tile[0]).push(tile);
      });
      var columns = Array.from(byProduct.values());
      var woven = [];
      for (var round = 0; woven.length < list.length; round++) {
        for (var c = 0; c < columns.length; c++) {
          if (columns[c][round]) woven.push(columns[c][round]);
        }
      }
      return woven;
    }
    return list;
  }

  /* The tile's own name - the product's, plus the fabric on a split listing, which
     is what the card prints and therefore what an A-Å sort has to agree with. */
  function titleOf(tile) {
    var entry = index.p[tile[0]];
    var variant = tile[1] >= 0 ? entry.v[tile[1]] : null;
    return entry.t + (variant ? ' - ' + variant[1] : '');
  }

  /* How far below its compare-at price a tile is, as a fraction. 0 when it is not
     reduced, so full-price tiles fall to the end of a discount sort together. */
  function discountOf(tile) {
    var prices = pricesOf(tile);
    var price = prices[0];
    var compareAt = prices[1];
    if (!compareAt || !price || compareAt <= price) return 0;
    return (compareAt - price) / compareAt;
  }

  /* --- Money ---------------------------------------------------------------- */

  /* Always from maahoTilePrices, which the section renders on every request.
     The index is a rebuilt file and a price in it would be yesterday's. */
  function pricesOf(tile) {
    return (window.maahoTilePrices || {})[index.p[tile[0]].h] || [];
  }

  function priceOf(tile) {
    var price = pricesOf(tile)[0];
    return typeof price === 'number' ? price : 0;
  }

  /* shop.money_format, so the grid reads the way the rest of the shop does. */
  function money(cents) {
    var amount = (cents / 100).toLocaleString(document.documentElement.lang || 'da', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    var format = window.maahoMoneyFormat;
    return format ? format.replace(/\{\{\s*\w+\s*\}\}/, amount) : amount + ' kr';
  }

  /* --- Rendering ------------------------------------------------------------ */

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  /* Must match snippets/maaho-collection-card.liquid: same widths, same sizes, or
     the two halves of the grid download different images for the same tile. */
  function image(name, className, alt) {
    var picture = element('img', className);
    var base = cdn + name + '?width=';
    picture.src = base + '600';
    picture.srcset = [200, 300, 450, 600].map(function (width) {
      return base + width + ' ' + width + 'w';
    }).join(', ');
    picture.sizes = '(max-width: 767px) 46vw, (max-width: 1100px) 30vw, 310px';
    picture.alt = alt;
    picture.loading = 'lazy';
    /* No width/height: .product-card-media fixes the box with aspect-ratio and
       the image fills it, so there is nothing to reserve and nothing to guess. */
    return picture;
  }

  /* Must render what snippets/maaho-collection-card.liquid renders - the two draw
     the same grid, one for the first paint and one for every click after it, and
     a tile that loses its Gratis stofprøve button halfway through a visit is the
     kind of difference nobody reports. */
  function card(tile, position) {
    var entry = index.p[tile[0]];
    var variant = tile[1] >= 0 ? entry.v[tile[1]] : null;
    var picture = variant && variant[2] ? variant[2] : entry.i;
    var hover = variant ? variant[4] : entry.hi;
    var url = '/products/' + entry.h + (variant ? '?variant=' + (entry.v0 + variant[0]) : '');
    var title = entry.t + (variant ? ' - ' + variant[1] : '');
    var prices = pricesOf(tile);
    var price = prices[0];
    var compareAt = prices[1];

    var item = element('div', 'collection-grid-item');
    var cardElement = element('div', 'product-card');

    var media = element('a', 'product-card-media');
    media.href = url;
    if (picture) {
      var main = image(picture, 'product-card-image', title);
      /* The first row is what the visitor is waiting for; the rest can wait. */
      if (position < 12) {
        main.loading = 'eager';
        main.setAttribute('fetchpriority', 'high');
      }
      media.appendChild(main);
    }
    if (hover && canHover) {
      var hoverPicture = image(hover, 'product-card-image product-card-image-hover', '');
      hoverPicture.setAttribute('aria-hidden', 'true');
      media.appendChild(hoverPicture);
    }
    if (compareAt > price) {
      var percent = Math.floor((compareAt - price) * 100 / compareAt);
      media.appendChild(element('span', 'product-card-badge', '-' + percent + '%'));
    }
    cardElement.appendChild(media);

    var body = element('div', 'product-card-body');

    var heading = element('a', 'product-card-title');
    heading.href = url;
    heading.appendChild(element('h3', null, title));
    body.appendChild(heading);

    var subtitle = (window.maahoTileSubtitles || {})[entry.h];
    if (subtitle) body.appendChild(element('p', 'product-card-subtitle', subtitle));

    var priceLine = element('p', 'product-card-price');
    priceLine.appendChild(document.createTextNode(typeof price === 'number' ? money(price) : ''));
    if (compareAt > price) {
      priceLine.appendChild(element('s', 'product-card-price-compare', money(compareAt)));
    }
    body.appendChild(priceLine);

    var actions = element('div', 'product-card-actions');
    var orderButton = element('a', 'product-card-button product-card-button-order', strings.order_now || '');
    orderButton.href = url;
    actions.appendChild(orderButton);

    /* Gratis stofprøve - paired to the fabric this tile shows, so a Nature tile
       orders the Nature swatch. A fabric PrestaShop paired no sample to gets no
       button. entry.sv is the fallback for a product paired as a whole. */
    var sampleVariantId = variant ? variant[5] : entry.sv;
    var sampleColour = variant ? variant[1] : '';
    if (sampleVariantId) {
      /* .js-sample is delegated on the document by assets/maaho-sample.js, so the
         button works as soon as it is in the page. */
      var sample = element('button', 'product-card-button product-card-button-sample js-sample',
        strings.order_free_sample || '');
      sample.type = 'button';
      sample.setAttribute('data-sample-variant', sampleVariantId);
      sample.setAttribute('data-sample-colour', sampleColour);
      actions.appendChild(sample);
    }

    body.appendChild(actions);
    cardElement.appendChild(body);
    item.appendChild(cardElement);
    return item;
  }

  function paginate(total, current) {
    var pages = Math.max(1, Math.ceil(total / perPage));
    var nav = root.querySelector('[data-filter-pagination]');

    if (pages < 2) {
      if (nav) nav.remove();
      return pages;
    }
    if (!nav) {
      nav = element('nav', 'collection-pagination');
      nav.setAttribute('data-filter-pagination', '');
      nav.setAttribute('aria-label', strings.pagination_label || 'Pagination');
      grid.insertAdjacentElement('afterend', nav);
    }
    nav.textContent = '';

    /* 2.280 tiles is 64 pages; a link for each is unusable, so only the ends and
       a window around the current page are shown. Four consecutive pages at each
       end, so a five-page collection never shows an ellipsis hiding one page. */
    var wanted = new Set([1, pages, current, current - 1, current + 1]);
    if (current <= 4) { wanted.add(2); wanted.add(3); wanted.add(4); }
    if (current >= pages - 3) { wanted.add(pages - 1); wanted.add(pages - 2); wanted.add(pages - 3); }

    var previous = 0;
    Array.from(wanted).filter(function (n) { return n >= 1 && n <= pages; })
      .sort(function (a, b) { return a - b; })
      .forEach(function (number) {
        if (number - previous > 1) {
          nav.appendChild(element('span', 'collection-pagination-gap', '…'));
        }
        previous = number;

        var link;
        if (number === current) {
          link = element('span', 'collection-pagination-page', number);
          link.setAttribute('aria-current', 'page');
        } else {
          link = element('a', 'collection-pagination-page', number);
          link.href = pageUrl(number);
          if (strings.page_label) {
            link.setAttribute('aria-label', strings.page_label.replace('%PAGE%', number));
          }
        }
        link.setAttribute('data-page', number);
        nav.appendChild(link);
      });

    return pages;
  }

  function pageUrl(number) {
    var parameters = new URLSearchParams(window.location.search);
    if (number > 1) parameters.set('page', number); else parameters.delete('page');
    var query = parameters.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  function count(total) {
    var node = root.querySelector('[data-filter-count]');
    if (!node) return;
    var template = total === 1 ? strings.count_one : strings.count_other;
    node.textContent = template ? template.replace('%COUNT%', total) : total + '';
  }

  /* --- The sidebar ---------------------------------------------------------- */

  /* Server-rendered and left in place: which values exist does not change with a
     click, only which of them are chosen and where each link now points. */
  function markSidebar(state) {
    var chosen = {};
    state.tags.forEach(function (tag) {
      var part = splitTag(tag);
      if (part) (chosen[part.key] = chosen[part.key] || []).push(part.value);
    });

    root.querySelectorAll('.collection-filter-option').forEach(function (option) {
      var key = option.getAttribute('data-facet');
      var slug = option.getAttribute('data-slug');
      var tag = key + '-' + slug;
      var on = (chosen[key] || []).indexOf(slug) !== -1;

      option.classList.toggle('is-chosen', on);
      option.setAttribute('aria-current', String(on));
      option.href = collectionUrl(on
        ? state.tags.filter(function (t) { return t !== tag; })
        : state.tags.concat([tag]));
    });

    chips(state);

    root.querySelectorAll('.collection-filters-clear')
      .forEach(function (link) { link.href = collectionUrl([]); });
  }

  /* One chip per tag, reading "Farve: Blå". Both halves are read off the sidebar
     option the tag belongs to - matched on its own slug, not on the facet, or two
     colours would both be labelled with whichever was first. */
  function chips(state) {
    var wrapper = root.querySelector('[data-filter-chips]');
    var list = root.querySelector('[data-filter-chips-list]');
    if (!wrapper || !list) return;

    wrapper.hidden = state.tags.length === 0;
    list.textContent = '';

    state.tags.forEach(function (tag) {
      var part = splitTag(tag);
      var option = part && root.querySelector('.collection-filter-option[data-facet="'
        + part.key + '"][data-slug="' + part.value + '"]');
      var group = part && root.querySelector('[data-facet-group="' + part.key
        + '"] .collection-filter-group-heading');

      var label = option ? option.getAttribute('data-value') : tag;
      var chip = element('a', 'collection-active-filter',
        group ? group.textContent.trim() + ': ' + label : label);
      chip.href = collectionUrl(state.tags.filter(function (t) { return t !== tag; }));
      list.appendChild(chip);
    });
  }

  function collectionUrl(tags) {
    var path = window.location.pathname.split('/');
    var at = path.indexOf('collections');
    var base = at >= 0 ? path.slice(0, at + 2).join('/') : window.location.pathname;
    var parameters = new URLSearchParams(window.location.search);
    parameters.delete('page');
    var query = parameters.toString();
    return base + (tags.length ? '/' + tags.join('+') : '') + (query ? '?' + query : '');
  }

  /* --- Drawing a state ------------------------------------------------------ */

  function draw() {
    var state = readState();
    var list = order(filter(state), state.sort);
    var pages = Math.max(1, Math.ceil(list.length / perPage));
    var page = Math.min(Math.max(1, state.page), pages);

    var fragment = document.createDocumentFragment();
    var start = (page - 1) * perPage;
    list.slice(start, start + perPage).forEach(function (tile, position) {
      fragment.appendChild(card(tile, position));
      if (banner && position + 1 === BANNER_AFTER_TILE) fragment.appendChild(banner);
    });

    if (!list.length) {
      fragment.appendChild(element('p', 'collection-empty', strings.no_results || ''));
    }

    grid.textContent = '';
    grid.appendChild(fragment);
    count(list.length);
    paginate(list.length, page);
    markSidebar(state);

    var announcer = root.querySelector('[data-filter-announcer]');
    var counter = root.querySelector('[data-filter-count]');
    if (announcer && counter) announcer.textContent = counter.textContent;
  }

  function go(url, scroll) {
    history.pushState(null, '', url);
    root.classList.add('is-loading');
    return ensure().then(function () {
      draw();
      root.classList.remove('is-loading');
      if (scroll) window.scrollTo({top: 0, behavior: 'smooth'});
    }).catch(function () {
      /* No index, no filtering here - let the server answer as it always did. */
      root.classList.remove('is-loading');
      window.location.href = url;
    });
  }

  /* --- Events --------------------------------------------------------------- */

  var LINKS = '.collection-filter-option, .collection-active-filter,' +
    ' .collection-pagination-page, .collection-filters-clear,' +
    ' .collection-active-filters-clear';

  root.addEventListener('click', function (event) {
    var link = event.target.closest(LINKS);
    if (!link || !link.href || !root.contains(link)) return;
    /* Let the browser handle a new tab, a download or a modified click. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target === '_blank') return;

    event.preventDefault();
    go(link.getAttribute('href'), link.classList.contains('collection-pagination-page'));
  });

  root.addEventListener('change', function (event) {
    var select = event.target.closest('[data-filter-sort]');
    if (!select) return;
    var parameters = new URLSearchParams(window.location.search);
    parameters.set('sort_by', select.value);
    parameters.delete('page');
    go(window.location.pathname + '?' + parameters.toString(), false);
  });

  window.addEventListener('popstate', function () {
    ensure().then(draw).catch(function () {});
  });

  /* The grid is hidden by [data-awaiting-order] until the collection's own order
     is applied. Cleared whatever happens - a failed index must show the server's
     tiles, not an empty page. */
  function revealGrid() {
    grid.removeAttribute('data-awaiting-order');
  }

  /* Unfiltered page 1 holds the right 36 tiles but, on a collection with its own
     order, in the server's sequence: Shopify pages by product and cannot interleave
     one product's fabrics with another's. Anything else - a shared filtered link, a
     bookmarked page 3, a sort - has to be counted in tiles, which only this side
     can do. Both are drawn from the cached index. */
  buildSortDropdown();

  /* Orders the server cannot produce. Shopify sorts whole products, so a sort that
     reads a TILE - its fabric name, its discount - or that weaves products together
     has to be drawn from the index even on an unfiltered page 1. */
  var CLIENT_ONLY_SORTS = ['interleave', 'title-ascending', 'title-descending',
    'discount-descending'];

  var initial = readState();
  if (initial.tags.length || initial.page > 1 || CLIENT_ONLY_SORTS.indexOf(initial.sort) > -1) {
    ensure().then(draw).catch(function () {}).then(revealGrid);
  } else {
    ensure().then(function () {
      if (index.o) draw();
    }).catch(function () {}).then(revealGrid);
  }

  /* --- Mobile drawer and the group accordion -------------------------------- */
  /* Bound before the index is looked at, so both still work on a collection that
     has never been indexed. The sidebar itself becomes the drawer below 768px -
     same element, a class slides it in. */

  function bindDrawer() {
    function setDrawer(open) {
      var drawer = root.querySelector('[data-filter-drawer]');
      var overlay = root.querySelector('[data-filter-drawer-overlay]');
      if (!drawer) return;
      drawer.classList.toggle('is-open', open);
      if (overlay) overlay.hidden = !open;
      document.body.classList.toggle('collection-filter-drawer-active', open);
    }

    root.addEventListener('click', function (event) {
      var heading = event.target.closest('.collection-filter-group-heading');
      if (heading) {
        heading.setAttribute('aria-expanded',
          String(heading.getAttribute('aria-expanded') !== 'true'));
        return;
      }
      if (event.target.closest('[data-filter-drawer-open]')) setDrawer(true);
      if (event.target.closest('[data-filter-drawer-close]')) setDrawer(false);
      if (event.target.closest('[data-filter-drawer-overlay]')) setDrawer(false);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setDrawer(false);
    });
  }
}());
